// Visit-flag constants shared across API, admin, agency, and mobile.
// Mirrors the `needs_attention` and `missed_visit_*` columns on `visits`.
export const NEEDS_ATTENTION_REASONS = {
    could_not_complete_within_window: "Could not complete the visit within scheduled window",
    patient_refused: "Patient refused the visit",
    patient_wants_to_reschedule: "Patient asked to reschedule",
    patient_unreachable_by_phone: "Patient unreachable by phone",
    patient_unreachable_at_address: "Patient unreachable at address",
    cannot_schedule_before_deadline: "Cannot schedule before deadline"
};
// `taskhealth` = Task Health's own operational error caused the miss (e.g. we
// rescheduled or cancelled the visit but never told the nurse, so she showed up
// to a visit that was no longer there). She is blameless and still earns travel
// pay (like patient/agency fault), but the AGENCY is not charged (like nurse
// fault) — the agency/nurse split for this fault lives in billing.service.
export const MISSED_VISIT_FAULTS = ["patient", "nurse", "agency", "taskhealth"];
// ─── On-site issue (nurse arrived but blocked) ───────────
// Mirrors the `on_site_issue` JSONB column on `visits`. The nurse picks one of
// these from the mobile "Issue with this visit" sheet; reporting it clocks her in
// (arrival proof) and wakes the manager bot, which runs the on-site playbook.
// Reasons mirror the legacy `task_issue_type` rn_portal codes (refused / didn't
// answer / not home / wrong address) — see docs/MISSED_VISIT_HANDLING_SPEC.md.
// `aide_not_here` (Oct 2026, Noel's list items 17 and 35): the home health aide
// the visit needs is not there. The bot holds her and asks the agency (spec
// §2.7); on a Supervisory Visit it is always a miss with agency fault (D6a).
export const ON_SITE_ISSUE_REASONS = {
    patient_didnt_answer: "Patient didn't answer",
    patient_not_home: "Patient not home",
    patient_refused: "Patient refusing the visit",
    wrong_address: "Wrong address",
    aide_not_here: "Aide not here",
    other: "Something else"
};
// Fault each on-site reason resolves to when it ends in a miss (D6 in the spec).
// Patient branches → "patient"; wrong address and an absent aide → "agency" (the
// address and the aide are the agency's). "other" has no fixed fault (the bot
// decides per situation) so it's intentionally absent here.
export const ON_SITE_ISSUE_MISS_FAULT = {
    patient_didnt_answer: "patient",
    patient_not_home: "patient",
    patient_refused: "patient",
    wrong_address: "agency",
    aide_not_here: "agency"
};
// ─── Supervisory Visit: no aide, no visit (D6a, 2026-10-12) ──
// On a Supervisory Visit the nurse comes to observe the home health aide, so a
// visit where the aide is not there is a MISSED visit with agency fault (the
// nurse gets travel pay, the agency is billed the agency-fault split), never a
// completed, fully billed one. The nurse app sends her to Report an issue
// ("Aide not here") instead of letting her finish the form, and the API refuses
// to complete a supervisory evaluation with the aide absent on these visits.
//
// Matched on visit_types.name, like `visitTypeAlwaysRequiresAide` on the API.
// Deliberately narrower than that helper: the Supervisory Evaluation SECTION also
// sits on SOC / reassessment / ROC visits (an aide check inside a patient visit),
// where an absent aide stays an RN-only sign-off, and the aide Performance
// Evaluation visit type is not part of this decision.
export function isSupervisoryVisitTypeName(name) {
    return /supervisory/i.test(name ?? "");
}
// Faults that still earn the nurse travel-time pay. Drives mobile list visibility.
// `taskhealth` earns travel pay too (she showed up, blameless) even though the
// agency is not charged for it — the agency-charge split lives in billing.service.
export const TRAVEL_PAY_MISSED_FAULTS = [
    "patient",
    "agency",
    "taskhealth"
];
export function getNeedsAttentionLabel(reason) {
    if (!reason) {
        return null;
    }
    return NEEDS_ATTENTION_REASONS[reason] ?? reason;
}
export function isTravelPayMissedFault(fault) {
    return fault === "patient" || fault === "agency" || fault === "taskhealth";
}
export function getOnSiteIssueLabel(reason) {
    if (!reason) {
        return null;
    }
    return ON_SITE_ISSUE_REASONS[reason] ?? reason;
}
//# sourceMappingURL=visit-flags.js.map